import { useEffect, useMemo, useRef, useState } from "react";
import {
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid,
  Button,
  Box,
  Paper,
} from "@mui/material";
import PropTypes from "prop-types";
import AudioPlayerComponent from "./AudioPlayer";
import {
  createQuestionForLevel,
  updateAudioFileForQuestion,
} from "../service/service";

const questionTypes = [
  "MC",
  "MCM",
  "ACIQ",
  "ACMC",
  "CT",
  "ACIQM",
  "UVI",
  "LAR",
  "CTA",
];

const hintTypes = ["audio", "text"];

const SINGLE_ANSWER_TYPES = new Set(["MC", "ACIQ", "ACMC", "CTA"]);
const MULTI_ANSWER_TYPES = new Set(["MCM", "ACIQM"]);
const QUESTION_TYPES_WITH_OPTIONS = new Set([
  "MC",
  "MCM",
  "ACIQ",
  "ACMC",
  "ACIQM",
  "CTA",
]);

const createEmptyOption = (type, index) =>
  type === "ACMC"
    ? {
        text: `Option ${index + 1}`,
        correct: false,
        audioUrl: "",
        audioFile: null,
      }
    : { text: "", correct: false };

const getInitialOptions = (type) => {
  if (!QUESTION_TYPES_WITH_OPTIONS.has(type)) {
    return [];
  }
  const defaultLength = type === "ACMC" ? 0 : 4;
  return Array.from({ length: defaultLength }, (_, optionIndex) =>
    createEmptyOption(type, optionIndex)
  );
};

function NewQuestionForm({
  courseId,
  unitId,
  levelId,
  levelOrder,
  nextOrder,
  onCancel,
  onSuccess,
}) {
  const [questionType, setQuestionType] = useState("");
  const [question, setQuestion] = useState("");
  const [noteBrief, setNoteBrief] = useState("");
  const [noteFull, setNoteFull] = useState("");
  const [phrase, setPhrase] = useState("");
  const [hintType, setHintType] = useState("");
  const [hint, setHint] = useState("");

  const [options, setOptions] = useState([]);
  const [questionAudio, setQuestionAudio] = useState({
    file: null,
    preview: "",
  });
  const [hintAudio, setHintAudio] = useState({ file: null, preview: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [formMessage, setFormMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const questionAudioInputRef = useRef(null);
  const hintAudioInputRef = useRef(null);

  const handleAddOption = () => {
    setOptions((prev) => [
      ...prev,
      createEmptyOption(questionType, prev.length),
    ]);
  };

  const handleOptionTextChange = (index, value) => {
    setOptions((prev) =>
      prev.map((option, optionIndex) =>
        optionIndex === index ? { ...option, text: value } : option
      )
    );
  };

  const handleOptionCorrectChange = (index, checked) => {
    setOptions((prev) =>
      prev.map((option, optionIndex) => {
        if (optionIndex !== index) {
          if (SINGLE_ANSWER_TYPES.has(questionType)) {
            return { ...option, correct: false };
          }
          return option;
        }

        return { ...option, correct: checked };
      })
    );
  };

  const handleRemoveOption = (index) => {
    setOptions((prev) =>
      prev.filter((_, optionIndex) => optionIndex !== index)
    );
  };

  const resetQuestionAudio = () => {
    setQuestionAudio((prev) => {
      if (prev.preview) {
        URL.revokeObjectURL(prev.preview);
      }
      return { file: null, preview: "" };
    });
    if (questionAudioInputRef.current) {
      questionAudioInputRef.current.value = "";
    }
  };

  const resetHintAudio = () => {
    setHintAudio((prev) => {
      if (prev.preview) {
        URL.revokeObjectURL(prev.preview);
      }
      return { file: null, preview: "" };
    });
    if (hintAudioInputRef.current) {
      hintAudioInputRef.current.value = "";
    }
  };

  const handleQuestionAudioChange = (file) => {
    setFieldErrors((prev) => ({ ...prev, questionAudio: undefined }));
    setFormMessage(null);
    setQuestionAudio((prev) => {
      if (prev.preview) {
        URL.revokeObjectURL(prev.preview);
      }
      if (!file) {
        return { file: null, preview: "" };
      }
      return { file, preview: URL.createObjectURL(file) };
    });
  };

  const handleHintAudioChange = (file) => {
    setFieldErrors((prev) => ({ ...prev, hintAudio: undefined }));
    setFormMessage(null);
    setHintAudio((prev) => {
      if (prev.preview) {
        URL.revokeObjectURL(prev.preview);
      }
      if (!file) {
        return { file: null, preview: "" };
      }
      return { file, preview: URL.createObjectURL(file) };
    });
  };

  const handleOptionAudioChange = (index, file) => {
    setOptions((prev) =>
      prev.map((option, optionIndex) => {
        if (optionIndex !== index) {
          return option;
        }
        if (option.audioUrl) {
          URL.revokeObjectURL(option.audioUrl);
        }
        if (!file) {
          return { ...option, audioFile: null, audioUrl: "" };
        }
        return {
          ...option,
          audioFile: file,
          audioUrl: URL.createObjectURL(file),
        };
      })
    );
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!questionType) {
      nextErrors.questionType = "Select a question type.";
    }
    if (!question.trim()) {
      nextErrors.question = "Question text is required.";
    }

    if (QUESTION_TYPES_WITH_OPTIONS.has(questionType)) {
      const filledOptions = options.filter((option) => option.text.trim());
      if (filledOptions.length < 2 && questionType !== "ACMC") {
        nextErrors.options = "Provide at least two options.";
      }
      const selectedOptions = options.filter((option) => option.correct);
      if (!selectedOptions.length) {
        nextErrors.options = "Select at least one correct option.";
      }
      if (SINGLE_ANSWER_TYPES.has(questionType) && selectedOptions.length > 1) {
        nextErrors.options = "Only one option can be marked correct.";
      }
      if (questionType === "ACMC") {
        const hasAudioForEach = options.every(
          (option) => option.text.trim() && option.audioFile
        );
        if (!hasAudioForEach) {
          nextErrors.options = "Provide audio for each ACMC option.";
        }
      }
    }

    if (questionType === "ACIQM" && !questionAudio.file) {
      nextErrors.questionAudio = "Upload question audio for ACIQM.";
    }

    if (["UVI", "LAR"].includes(questionType) && !phrase.trim()) {
      nextErrors.phrase = "Phrase is required for this question type.";
    }

    if (hintType === "text" && !hint.trim()) {
      nextErrors.hint = "Hint text is required.";
    }

    if (hintType === "audio" && !hintAudio.file) {
      nextErrors.hintAudio = "Upload a hint audio file.";
    }

    setFieldErrors(nextErrors);
    return !Object.keys(nextErrors).length;
  };

  const buildAnswer = () => {
    const selected = options.filter((option) => option.correct);
    if (!selected.length) {
      return "";
    }
    if (questionType === "ACMC") {
      return selected[0]?.text || "";
    }
    if (MULTI_ANSWER_TYPES.has(questionType)) {
      return selected.map((option) => option.text).join("; ");
    }
    return selected[0]?.text || "";
  };

  const buildPayload = () => {
    const answer = buildAnswer();
    const trimmedOptions = options
      .filter((option) => option.text.trim())
      .map((option) => ({
        ...option,
        text: option.text.trim(),
      }));

    let optionsForPayload = null;
    if (QUESTION_TYPES_WITH_OPTIONS.has(questionType)) {
      if (questionType === "ACMC") {
        optionsForPayload = trimmedOptions.reduce((acc, option) => {
          acc[option.text] = "";
          return acc;
        }, {});
      } else {
        optionsForPayload = trimmedOptions.map((option) => option.text);
      }
    }

    const payload = {
      order: nextOrder,
      questionType,
      question: question.trim(),
      Answer: answer,
      options: optionsForPayload,
      noteBrief: noteBrief.trim(),
      noteFull: noteFull.trim(),
      parentUnitId: unitId,
      parentCourseId: courseId,
      parentLevelId: levelId,
      hintType,
      hint: hintType === "text" ? hint.trim() : undefined,
      hintUrl: hintType === "audio" ? "" : undefined,
      phrase: ["UVI", "LAR"].includes(questionType)
        ? phrase.trim()
        : undefined,
      isQuestionArchived: false,
      url: ["ACIQ", "CTA", "CT", "ACIQM"].includes(questionType) ? "" : undefined,
    };

    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    if (!QUESTION_TYPES_WITH_OPTIONS.has(questionType)) {
      payload.options = null;
    }

    const attachments = {
      questionAudioFile: questionAudio.file || null,
      hintAudioFile: hintAudio.file || null,
      optionAudioFiles:
        questionType === "ACMC"
          ? trimmedOptions.map((option) => ({
              text: option.text,
              file: option.audioFile,
            }))
          : [],
    };

    return { payload, attachments };
  };

  const handleCreateQuestion = async () => {
    if (!validateForm()) {
      return;
    }
    const { payload, attachments } = buildPayload();
    setIsSubmitting(true);
    setFormMessage(null);
    try {
      const response = await createQuestionForLevel(payload, attachments);
      if (!response?.success) {
        const errorMessage =
          response?.error?.message ||
          response?.message ||
          "Failed to create question.";
        setFormMessage({
          type: "error",
          text: errorMessage,
        });
        return;
      }

      onSuccess?.(response.data, { response });
      resetForm();
      setFormMessage({
        type: "info",
        text: response.message || "Question created successfully.",
      });
    } catch (error) {
      setFormMessage({
        type: "error",
        text: error?.message || "Failed to create question.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setQuestionType("");
    setQuestion("");
    setNoteBrief("");
    setNoteFull("");
    setPhrase("");
    setHintType("");
    setHint("");
    setOptions([]);
    resetQuestionAudio();
    resetHintAudio();
    setFieldErrors({});
  };

  // const handleAudioSubmit = async (event, fieldKey) => {
  //   event.preventDefault();
  //   const audioState = fieldKey === "questionAudio" ? questionAudio : hintAudio;

  //   if (!audioState.file) {
  //     setFieldErrors((prev) => ({
  //       ...prev,
  //       [fieldKey]: "Select an MP3 file before uploading.",
  //     }));
  //     return;
  //   }

  //   try {
  //     const formData = new FormData();
  //     formData.append("file", audioState.file);
  //     formData.append("courseId", courseId);
  //     formData.append("unitId", unitId);
  //     formData.append("levelId", levelId);
  //     formData.append("fieldToUpdate", fieldKey);
  //     await updateAudioFileForQuestion(formData);
  //     setFormMessage({
  //       type: "info",
  //       text: `${fieldKey === "questionAudio" ? "Question" : "Hint"} audio ready to be sent to the backend.`,
  //     });
  //   } catch (error) {
  //     setFormMessage({
  //       type: "error",
  //       text: error?.message || "Audio upload placeholder failed.",
  //     });
  //   }
  // };

  // const handleQuestionAudioSubmit = (event) =>
  //   handleAudioSubmit(event, "questionAudio");

  // const (event) => handleAudioSubmit(event, "") = (event) =>
  //   handleAudioSubmit(event, "hintAudio");

  useEffect(() => {
    setOptions(getInitialOptions(questionType));
    setFieldErrors((prev) => ({ ...prev, options: undefined }));
  }, [questionType]);

  useEffect(() => {
    if (!hintTypes.includes(hintType)) {
      setHint("");
      resetHintAudio();
    }
    if (hintType !== "text") {
      setHint("");
    }
    if (hintType !== "audio") {
      resetHintAudio();
    }
  }, [hintType]);

  useEffect(() => {
    if (!["UVI", "LAR"].includes(questionType)) {
      setPhrase("");
    }
  }, [questionType]);

  const questionLabel = useMemo(() => {
    if (levelOrder && nextOrder) {
      return `L${levelOrder} • Q${nextOrder}`;
    }
    if (nextOrder) {
      return `Question ${nextOrder}`;
    }
    return "Question";
  }, [levelOrder, nextOrder]);

  return (
    <Paper sx={{ padding: 2, marginTop: 2 }} elevation={3}>
      <Typography sx={{ mt: 2, mb: 2 }} variant="h6">
        {questionLabel}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Question Type</InputLabel>
            <Select
              value={questionType}
              label="Question Type"
              onChange={(e) => setQuestionType(e.target.value)}
            >
              {questionTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {fieldErrors.questionType && (
            <Typography color="error" variant="caption">
              {fieldErrors.questionType}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            label="Question"
            variant="outlined"
            fullWidth
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />
          {fieldErrors.question && (
            <Typography color="error" variant="caption">
              {fieldErrors.question}
            </Typography>
          )}
        </Grid>

        {["ACIQ", "CTA", "CT", "ACIQM"].includes(questionType) && (
          <div className="audio-container">
            {questionAudio.preview ? (
              <AudioPlayerComponent audioUrl={questionAudio.preview} />
            ) : (
              <Typography variant="caption" sx={{ ml: 4 }}>
                No audio selected
              </Typography>
            )}
            <form
              style={{ marginLeft: "20px" }}
              // onSubmit={(event) => handleAudioSubmit(event, "questionAudio")}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button variant="outlined" component="label" color="info">
                  Choose File
                  <input
                    hidden
                    type="file"
                    accept=".mp3"
                    ref={questionAudioInputRef}
                    onChange={(event) =>
                      handleQuestionAudioChange(event.target.files?.[0] || null)
                    }
                  />
                </Button>
                {/* <Button type="submit" variant="contained" sx={{ ml: 2 }}>
                  Upload
                </Button> */}
              </Box>
            </form>
            {fieldErrors.questionAudio && (
              <Typography color="error" variant="caption">
                {fieldErrors.questionAudio}
              </Typography>
            )}
          </div>
        )}

        {/* {console.log("question options ----", question.Answer)} */}

        {!["ACMC", "UVI", "CT", "LAR"].includes(questionType) && (
          <>
            <Grid item xs={12}>
              <Table sx={{ background: "primary.lighterShade" }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Option Text</TableCell>
                    <TableCell align="right">Is Correct Answer</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {options &&
                    options.map((option, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            value={option.text}
                            fullWidth
                            onChange={(e) =>
                              handleOptionTextChange(index, e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell style={{ width: 160 }} align="center">
                          <Checkbox
                            checked={option.correct}
                            onChange={(e) =>
                              handleOptionCorrectChange(index, e.target.checked)
                            }
                          />
                        </TableCell>
                        <TableCell style={{ width: 160 }} align="right">
                          <Button
                            variant="contained"
                            color="secondary"
                            sx={{ mt: 2 }}
                            onClick={() => handleRemoveOption(index)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <Button
                variant="contained"
                color="info"
                sx={{ mt: 2 }}
                onClick={handleAddOption}
              >
                Add Option
              </Button>
            </Grid>
            {fieldErrors.options && (
              <Grid item xs={12}>
                <Typography color="error" variant="caption">
                  {fieldErrors.options}
                </Typography>
              </Grid>
            )}
          </>
        )}

        {questionType === "ACMC" ? (
          <>
            <Grid item xs={12}>
              <Table sx={{ background: "primary.lighterShade" }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Option</TableCell>
                    <TableCell>Audio Clip</TableCell>
                    <TableCell align="center">Is Correct Answer</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {options.map((option, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          value={option.text}
                          fullWidth
                          onChange={(e) =>
                            handleOptionTextChange(index, e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {option.audioUrl ? (
                            <AudioPlayerComponent audioUrl={option.audioUrl} />
                          ) : (
                            <Typography variant="body2" sx={{ mr: 2 }}>
                              No audio selected
                            </Typography>
                          )}
                          <Button component="label" variant="outlined">
                            Upload
                            <input
                              hidden
                              type="file"
                              accept=".mp3"
                              onChange={(event) => {
                                const file = event.target.files?.[0] || null;
                                handleOptionAudioChange(index, file);
                              }}
                            />
                          </Button>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={option.correct}
                          onChange={(e) =>
                            handleOptionCorrectChange(index, e.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          color="secondary"
                          sx={{ mt: 2 }}
                          onClick={() => handleRemoveOption(index)}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button
                variant="contained"
                color="info"
                sx={{ mt: 2 }}
                onClick={handleAddOption}
              >
                Add Option
              </Button>
            </Grid>
            {fieldErrors.options && (
              <Grid item xs={12}>
                <Typography color="error" variant="caption">
                  {fieldErrors.options}
                </Typography>
              </Grid>
            )}
          </>
        ) : null}

        <Grid item xs={12} md={6}>
          <TextField
            label="Note Brief"
            variant="outlined"
            fullWidth
            value={noteBrief}
            onChange={(e) => setNoteBrief(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Note Full"
            variant="outlined"
            fullWidth
            value={noteFull}
            onChange={(e) => setNoteFull(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Hint Type</InputLabel>
            <Select
              value={hintType}
              onChange={(e) => setHintType(e.target.value)}
            >
              {hintTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {(questionType === "UVI" || questionType === "LAR") && (
          <Grid item xs={12} md={4}>
            <TextField
              label="Phrase"
              variant="outlined"
              fullWidth
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
            />
            {fieldErrors.phrase && (
              <Typography color="error" variant="caption">
                {fieldErrors.phrase}
              </Typography>
            )}
          </Grid>
        )}

        {hintType === "text" && (
          <Grid item xs={12} md={8}>
            <TextField
              label="Hint"
              variant="outlined"
              fullWidth
              value={hint}
              onChange={(e) => setHint(e.target.value)}
            />
            {fieldErrors.hint && (
              <Typography color="error" variant="caption">
                {fieldErrors.hint}
              </Typography>
            )}
          </Grid>
        )}

        {hintType === "audio" ? (
          <div>
            <Grid item xs={12} md={8}>
              <InputLabel sx={{ marginLeft: 2, marginTop: 1 }}>
                Hint Audio
              </InputLabel>
            </Grid>
            <div className="audio-container">
              {hintAudio.preview ? (
                <AudioPlayerComponent audioUrl={hintAudio.preview} />
              ) : (
                <Typography variant="caption" sx={{ ml: 4 }}>
                  No audio selected
                </Typography>
              )}
              <form
                style={{ marginLeft: "20px" }}
                // onSubmit={(event) => handleAudioSubmit(event, "hintAudio")}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Button variant="outlined" component="label" color="info">
                    Choose File
                    <input
                      hidden
                      type="file"
                      accept=".mp3"
                      ref={hintAudioInputRef}
                      onChange={(event) =>
                        handleHintAudioChange(event.target.files?.[0] || null)
                      }
                    />
                  </Button>
                  {/* <Button type="submit" variant="contained" sx={{ ml: 2 }}>
                    Upload
                  </Button> */}
                </Box>
              </form>
            </div>
            {fieldErrors.hintAudio && (
              <Typography color="error" variant="caption">
                {fieldErrors.hintAudio}
              </Typography>
            )}
          </div>
        ) : (
          ""
        )}
        <br />
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              resetForm();
              onCancel?.();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            onClick={handleCreateQuestion}
          >
            {isSubmitting ? "Saving..." : "Save Question"}
          </Button>
        </Grid>
        {formMessage && (
          <Grid item xs={12}>
            <Typography
              variant="body2"
              color={
                formMessage.type === "error"
                  ? "error"
                  : formMessage.type === "warning"
                  ? "warning.main"
                  : "success.main"
              }
              sx={{ mt: 1 }}
            >
              {formMessage.text}
            </Typography>
          </Grid>
        )}
      </Grid>
      {/* ))} */}
    </Paper>
  );
}

NewQuestionForm.propTypes = {
  courseId: PropTypes.string.isRequired,
  unitId: PropTypes.string.isRequired,
  levelId: PropTypes.string.isRequired,
  levelOrder: PropTypes.number,
  nextOrder: PropTypes.number,
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
};

NewQuestionForm.defaultProps = {
  levelOrder: undefined,
  nextOrder: undefined,
  onCancel: undefined,
  onSuccess: undefined,
};

export default NewQuestionForm;
